import React, { useEffect, useMemo, useState } from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { supabase } from "./utils/supabaseClient";


/* =========================================================
   Devuelve YYYY-MM-DD usando la fecha LOCAL del navegador.

   Evita problemas por UTC.
========================================================= */
function getTodayLocal() {
  const now = new Date();

  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}


export default function ActivateCertificate() {

  const { token } = useParams();
  const navigate = useNavigate();

  const today = useMemo(
    () => getTodayLocal(),
    []
  );


  /* =========================================================
     DATOS DEL EQUIPO
  ========================================================= */

  const [unit, setUnit] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");


  /* =========================================================
     DATOS QUE PUEDE CAPTURAR EL COMPRADOR
  ========================================================= */

  const [purchaseDate, setPurchaseDate] =
    useState(today);

  const [customerName, setCustomerName] =
    useState("");

  const [branchName, setBranchName] =
    useState("");

  const [street, setStreet] = useState("");
  const [neighborhood, setNeighborhood] =
    useState("");

  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  const [postalCode, setPostalCode] =
    useState("");

  const [country, setCountry] =
    useState("México");

  const [confirmed, setConfirmed] =
    useState(false);


  /* =========================================================
     CONSULTAR UNIDAD POR TOKEN
  ========================================================= */

  useEffect(() => {

    let mounted = true;


    const loadUnit = async () => {

      setLoading(true);
      setError("");


      const {
        data,
        error: rpcError,
      } = await supabase.rpc(
        "get_serial_activation",
        {
          p_token: token,
        }
      );


      if (!mounted) return;


      if (rpcError || !data) {

        console.error(
          "Error consultando activación:",
          rpcError
        );

        setError(
          "El enlace de activación no es válido."
        );

        setLoading(false);

        return;
      }


      setUnit(data);
      setLoading(false);


      /* =====================================================
         Si ya fue activado, mandarlo directamente
         al certificado.

         El cliente NO vuelve a ver el formulario.
      ===================================================== */

      if (
        data.certificate_token
      ) {

        navigate(
          `/verify/${data.certificate_token}`,
          {
            replace: true,
          }
        );
      }
    };


    loadUnit();


    return () => {
      mounted = false;
    };

  }, [token, navigate]);


  /* =========================================================
     EMITIR CERTIFICADO
  ========================================================= */

  const handleActivate = async () => {

    setError("");


    /* Fecha obligatoria */
    if (!purchaseDate) {

      setError(
        "Selecciona la fecha de compra."
      );

      return;
    }


    /* Protección frontend contra fecha futura */
    if (purchaseDate > today) {

      setError(
        "La fecha de compra no puede ser posterior al día de hoy."
      );

      return;
    }


    if (!confirmed) {

      setError(
        "Confirma que la información capturada es correcta."
      );

      return;
    }


    setSaving(true);


    try {

      const {
        data,
        error: rpcError,
      } = await supabase.rpc(
        "activate_serial_certificate",
        {

          p_activation_token:
            token,

          p_purchase_date:
            purchaseDate,

          p_customer_name:
            customerName || null,

          p_branch_name:
            branchName || null,

          p_street:
            street || null,

          p_neighborhood:
            neighborhood || null,

          p_city:
            city || null,

          p_state:
            state || null,

          p_postal_code:
            postalCode || null,

          p_country:
            country || "México",
        }
      );


      if (rpcError) {

        console.error(
          "Error generando certificado:",
          rpcError
        );


        if (
          rpcError.message?.includes(
            "FECHA_FUTURA_NO_PERMITIDA"
          )
        ) {

          throw new Error(
            "La fecha de compra no puede ser posterior al día de hoy."
          );
        }


        throw rpcError;
      }


      if (!data?.certificate_token) {

        throw new Error(
          "El certificado fue procesado pero no se recibió el enlace de validación."
        );
      }


      /* =====================================================
         Certificado emitido.

         A partir de este momento el comprador ya NO
         puede editar sus datos.
      ===================================================== */

      navigate(
        `/verify/${data.certificate_token}`,
        {
          replace: true,
        }
      );

    } catch (err) {

      console.error(err);

      setError(
        err.message ||
          "No se pudo generar el certificado."
      );

    } finally {

      setSaving(false);

    }
  };


  /* =========================================================
     CARGANDO
  ========================================================= */

  if (loading) {

    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">

        <div className="bg-white shadow rounded-xl p-8 text-center">

          <div className="text-lg font-semibold">
            Consultando equipo…
          </div>

        </div>

      </div>
    );
  }


  /* =========================================================
     ERROR
  ========================================================= */

  if (error && !unit) {

    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">

        <div className="bg-white shadow rounded-xl p-8 max-w-lg text-center">

          <div className="text-xl font-bold text-red-600">
            Enlace no válido
          </div>

          <div className="text-gray-600 mt-3">
            {error}
          </div>

        </div>

      </div>
    );
  }


  const equipment = unit?.equipment || {};
  const product = equipment?.product || {};


  return (

    <div className="min-h-screen bg-gray-100 py-8 px-4">

      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-xl border p-6 md:p-8">


        {/* ===================================================
            ENCABEZADO
        =================================================== */}

        <div className="text-center mb-7">

          <h1 className="text-2xl font-bold">
            Activar certificado
          </h1>

          <p className="text-gray-600 text-sm mt-2">
            Complete los datos que desea que aparezcan
            en su certificado.
          </p>

        </div>


        {/* ===================================================
            EQUIPO - NO EDITABLE
        =================================================== */}

        <div className="bg-gray-50 border rounded-lg p-4 mb-6">

          <div className="text-xs text-gray-500">
            EQUIPO
          </div>

          <div className="font-semibold mt-1">

            {[
              product.marca,
              product.sku,
              product.descripcion,
            ]
              .filter(Boolean)
              .join(" ")}

          </div>


          <div className="text-xs text-gray-500 mt-4">
            NÚMERO DE SERIE
          </div>

          <div className="font-mono font-bold text-blue-700 mt-1 break-all">
            {unit.serial_number}
          </div>

        </div>


        {/* ===================================================
            FECHA COMPRA
        =================================================== */}

        <div className="mb-5">

          <label className="block font-medium mb-1">

            Fecha de compra
            <span className="text-red-600"> *</span>

          </label>

          <input
            type="date"
            value={purchaseDate}
            max={today}
            onChange={(event) =>
              setPurchaseDate(
                event.target.value
              )
            }
            className="border rounded-lg p-3 w-full"
          />

          <div className="text-xs text-gray-500 mt-1">
            La fecha no puede ser posterior al día de hoy.
          </div>

        </div>


        {/* ===================================================
            CLIENTE
        =================================================== */}

        <div className="mb-5">

          <label className="block font-medium mb-1">
            Nombre o razón social
            <span className="text-gray-400 font-normal">
              {" "} (opcional)
            </span>
          </label>

          <input
            value={customerName}
            onChange={(event) =>
              setCustomerName(
                event.target.value
              )
            }
            className="border rounded-lg p-3 w-full"
            placeholder="Ej. Farmacia ABC, Juan Pérez..."
          />

        </div>


        <div className="mb-5">

          <label className="block font-medium mb-1">
            Sucursal o establecimiento
            <span className="text-gray-400 font-normal">
              {" "} (opcional)
            </span>
          </label>

          <input
            value={branchName}
            onChange={(event) =>
              setBranchName(
                event.target.value
              )
            }
            className="border rounded-lg p-3 w-full"
            placeholder="Ej. Sucursal Centro"
          />

        </div>


        {/* ===================================================
            DOMICILIO OPCIONAL
        =================================================== */}

        <div className="border-t pt-5">

          <h2 className="font-semibold mb-4">
            Domicilio
            <span className="text-gray-400 font-normal">
              {" "} (opcional)
            </span>
          </h2>


          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

            <input
              value={street}
              onChange={(event) =>
                setStreet(
                  event.target.value
                )
              }
              className="border rounded-lg p-3"
              placeholder="Calle y número"
            />


            <input
              value={neighborhood}
              onChange={(event) =>
                setNeighborhood(
                  event.target.value
                )
              }
              className="border rounded-lg p-3"
              placeholder="Colonia"
            />


            <input
              value={city}
              onChange={(event) =>
                setCity(
                  event.target.value
                )
              }
              className="border rounded-lg p-3"
              placeholder="Ciudad"
            />


            <input
              value={state}
              onChange={(event) =>
                setState(
                  event.target.value
                )
              }
              className="border rounded-lg p-3"
              placeholder="Estado"
            />


            <input
              value={postalCode}
              onChange={(event) =>
                setPostalCode(
                  event.target.value
                )
              }
              className="border rounded-lg p-3"
              placeholder="Código postal"
            />


            <input
              value={country}
              onChange={(event) =>
                setCountry(
                  event.target.value
                )
              }
              className="border rounded-lg p-3"
              placeholder="País"
            />

          </div>

        </div>


        {/* ===================================================
            CONFIRMACIÓN FINAL
        =================================================== */}

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">

          <label className="flex items-start gap-3 cursor-pointer">

            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) =>
                setConfirmed(
                  event.target.checked
                )
              }
              className="mt-1"
            />

            <span className="text-sm">

              Confirmo que la información capturada es correcta.
              Una vez emitido el certificado,
              estos datos ya no podrán ser modificados desde esta página.

            </span>

          </label>

        </div>


        {/* ERROR */}

        {error && (

          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">

            {error}

          </div>

        )}


        {/* BOTÓN */}

        <button
          type="button"
          onClick={handleActivate}
          disabled={
            saving ||
            !confirmed ||
            !purchaseDate
          }
          className="w-full mt-6 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-lg text-lg"
        >

          {saving
            ? "Generando certificado…"
            : "Emitir mi certificado"}

        </button>


        <div className="text-xs text-center text-gray-500 mt-4">

          La vigencia se calcula automáticamente
          conforme al periodo establecido para este equipo.

        </div>

      </div>

    </div>

  );
}